import React, { useEffect, useState, type ReactNode } from 'react';
import {
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { KeyboardDoneAccessory } from './KeyboardDoneAccessory';

type Props = {
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
  /** Extra iOS offset when a header sits above this container. */
  offset?: number;
};

/**
 * Lifts overlay content above the software keyboard on iOS and Android.
 *
 * Transparent RN Modals do not inherit Activity `adjustResize` on Android, so
 * we pad by keyboard height there. iOS uses KeyboardAvoidingView (animated).
 * Each modal also mounts the iOS Done accessory (separate window from App root).
 */
export function KeyboardAvoidingContainer({ children, style, offset = 0 }: Props) {
  if (Platform.OS === 'web') {
    return <View style={[styles.fill, style]}>{children}</View>;
  }

  if (Platform.OS === 'ios') {
    return (
      <>
        <KeyboardAvoidingView
          style={[styles.fill, style]}
          behavior="padding"
          keyboardVerticalOffset={offset}
        >
          {children}
        </KeyboardAvoidingView>
        <KeyboardDoneAccessory />
      </>
    );
  }

  return (
    <>
      <AndroidKeyboardPad style={style}>{children}</AndroidKeyboardPad>
      <KeyboardDoneAccessory />
    </>
  );
}

function AndroidKeyboardPad({
  children,
  style,
}: {
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const show = Keyboard.addListener('keyboardDidShow', (e) => {
      setKeyboardHeight(e.endCoordinates.height);
    });
    const hide = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardHeight(0);
    });
    return () => {
      show.remove();
      hide.remove();
    };
  }, []);

  return (
    <View
      style={[styles.fill, style, keyboardHeight > 0 ? { paddingBottom: keyboardHeight } : null]}
    >
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  fill: {
    flex: 1,
  },
});
